/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Field, Form, Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import Button from 'components/atoms/Button'
import MaskInput from 'components/atoms/MaskInput'
import LandingLayout from 'components/landing/LandingLayout'
import { cureMdPaymentUrl, tsOfficePhone } from 'constants/misc'
import { dobMask, phoneNumberMask } from 'helpers/masks'
import { setPatientAccessToken } from 'helpers/patientSession'
import { phoneValidation } from 'helpers/validate'
import { useLookupPatientStatementMutation } from 'store/services/patientStatementsPublic'

const DOB_FORMAT = 'MM/DD/YYYY'

const validationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('Required'),
  lastName: Yup.string().trim().required('Required'),
  dob: Yup.string()
    .required('Required')
    .test('dob', `Enter a valid date (${DOB_FORMAT})`, (value) => {
      const parsed = moment(value, DOB_FORMAT, true)
      return parsed.isValid() && parsed.isSameOrBefore(moment(), 'day')
    }),
  phone: phoneValidation,
  email: Yup.string().trim().email('Enter a valid email address'),
})

const initialValues = { firstName: '', lastName: '', dob: '', phone: '', email: '' }

const NOT_FOUND_MESSAGE = 'No patient statement was found for the provided details.'
const GENERIC_ERROR_MESSAGE =
  'Something went wrong while looking up your statement. Please try again.'

const requiredMark = <span className="text-danger"> *</span>

/**
 * Public "find my bill" page behind the website's payment link. The patient
 * identifies themself, we find their newest open statement and drop them into
 * the statement portal with the DOB step already satisfied.
 */
const PatientPaymentLookup = () => {
  const navigate = useNavigate()
  const [lookupStatement, { isLoading }] = useLookupPatientStatementMutation()
  const [error, setError] = useState('')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleSubmit = async (values: typeof initialValues) => {
    setError('')

    try {
      const phone = values.phone.replace(/\D/g, '')
      const email = values.email.trim()
      const result = await lookupStatement({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        dob: moment(values.dob, DOB_FORMAT, true).format('YYYY-MM-DD'),
        ...(phone && { phone }),
        ...(email && { email }),
      }).unwrap()

      setPatientAccessToken(result.access_token)
      navigate(
        `/landing/verify-contract/statement?statementId=${result.statementId}&patientId=${result.patientId}`,
        { state: { verified: true } },
      )
    } catch (err) {
      const status = (err as { status?: number })?.status
      setError(status === 404 ? NOT_FOUND_MESSAGE : GENERIC_ERROR_MESSAGE)
    }
  }

  return (
    <LandingLayout>
      <div className="container py-4" css={{ maxWidth: 640 }}>
        <div
          css={{
            backgroundColor: '#fff',
            borderRadius: 8,
            border: '1px solid #e6e8eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            padding: 32,
          }}
        >
          <h1 css={{ fontSize: 28, fontWeight: 700, color: '#2c3e50', marginBottom: 4 }}>
            Patient Payment
          </h1>
          <p css={{ color: '#5f6368', marginBottom: 24 }}>
            Enter your details and we&rsquo;ll find your bill.
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange }) => (
              <Form noValidate>
                <div className="row">
                  <div className="col-sm-6 form-group">
                    <label htmlFor="firstName">First Name{requiredMark}</label>
                    <Field
                      type="text"
                      id="firstName"
                      name="firstName"
                      className="form-control"
                      placeholder="Enter first name"
                      autoComplete="given-name"
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="text-danger">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="col-sm-6 form-group">
                    <label htmlFor="lastName">Last Name{requiredMark}</label>
                    <Field
                      type="text"
                      id="lastName"
                      name="lastName"
                      className="form-control"
                      placeholder="Enter last name"
                      autoComplete="family-name"
                    />
                    {touched.lastName && errors.lastName && (
                      <div className="text-danger">{errors.lastName}</div>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-6 form-group">
                    <label htmlFor="dob">Date of Birth{requiredMark}</label>
                    <MaskInput
                      value={values.dob}
                      onChange={handleChange}
                      name="dob"
                      mask={dobMask}
                      placeholder={DOB_FORMAT}
                      autoComplete="bday"
                    />
                    {touched.dob && errors.dob && <div className="text-danger">{errors.dob}</div>}
                  </div>
                  <div className="col-sm-6 form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <MaskInput
                      value={values.phone}
                      onChange={handleChange}
                      name="phone"
                      mask={phoneNumberMask}
                      placeholder="(000) 000-0000"
                      autoComplete="tel"
                    />
                    {touched.phone && errors.phone && (
                      <div className="text-danger">{errors.phone}</div>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-6 form-group">
                    <label htmlFor="email">Email Address</label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="example@mail.com"
                      autoComplete="email"
                    />
                    {touched.email && errors.email && (
                      <div className="text-danger">{errors.email}</div>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <div>{error}</div>
                    {error === NOT_FOUND_MESSAGE && (
                      <div css={{ marginTop: 6, fontSize: 14 }}>
                        Please check your details and try again. If your statement came from our
                        previous billing system, use the CureMD link below, or call us at{' '}
                        {tsOfficePhone}.
                      </div>
                    )}
                  </div>
                )}

                <div
                  className="pt-3"
                  css={{
                    borderTop: '1px solid #e6e8eb',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    type="submit"
                    className="btn-primary"
                    label={isLoading ? 'Searching...' : 'Continue'}
                    disabled={isLoading}
                    css={{ minWidth: 140 }}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>

        <div css={{ textAlign: 'center', marginTop: 24, color: '#5f6368' }}>
          <p css={{ marginBottom: 8 }}>Have a statement from our previous billing system?</p>
          <a
            href={cureMdPaymentUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-secondary"
          >
            Pay through CureMD
          </a>
        </div>
      </div>
    </LandingLayout>
  )
}

export default PatientPaymentLookup
